// Global state to prevent multiple listeners
import { useI18n } from 'vue-i18n'

let isListenerSetup = false

export const useOfflineNotifications = () => {
  const { t } = useI18n()
  const isOnline = useState("isOnline", () => navigator?.onLine ?? true)
  const queuedReports = useState("queuedReports", () => 0)
  const notifications = useState("notifications", () => [])

  // Listen for online/offline events
  const updateOnlineStatus = async () => {
    const wasOffline = !isOnline.value
    isOnline.value = navigator.onLine
    console.log("📶 Online status changed:", isOnline.value)
  }

  onMounted(() => {
    // Only set up listeners once globally
    if (!isListenerSetup && import.meta.client) {
      console.log("🔔 Setting up offline notifications listeners")

      window.addEventListener("online", updateOnlineStatus)
      window.addEventListener("offline", updateOnlineStatus)

      // Listen for service worker messages - only once globally
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.addEventListener("message", handleSWMessage)
        console.log("👂 Service worker message listener added")
      }

      isListenerSetup = true
    }
  })

  
const handleSWMessage = (event) => {
  console.log("📨 Service Worker message received:", event.data)
  const { type, message, count } = event.data

  switch (type) {
    case "SUBMIT_QUEUED":
      queuedReports.value++
      showNotification(
        message || t("offline.queue_report"),
        "warning",
        5000
      )
      break

    case "SUBMIT_SUCCESS":
      showNotification(
        message || t("offline.submit_success"),
        "success",
        3000
      )
      break

    case "SYNC_SUCCESS":
      queuedReports.value = Math.max(0, queuedReports.value - (count || 1))
      showNotification(
        t("offline.sync_success", { count: count || 1 }),
        "success",
        4000
      )
      break

    case "SYNC_FAILURE":
      showNotification(
        t("offline.sync_failure"),
        "error",
        6000
      )
      break

    case "SYNC_STARTED":
      showNotification(
        t("offline.sync_started"),
        "info",
        3000
      )
      break

    case "QUEUE_CLEARED":
      showNotification(
        message || t("offline.queue_cleared"),
        "success",
        3000
      )
      break

    case "QUEUE_SIZE_CHANGED":
      queuedReports.value = count || 0
      console.log(`📊 Queue size updated: ${count}`)
      break

    default:
      console.log("🤷 Unknown service worker message type:", type)
  }
}


  const showNotification = (message, type = "info", duration = 4000) => {
    console.log(`🔔 Showing notification: ${message} (${type})`)
    const id = Date.now() + Math.random()
    const notification = {
      id,
      message,
      type,
      timestamp: new Date(),
    }

    notifications.value.push(notification)
    console.log("📊 Current notifications:", notifications.value.length)

    setTimeout(() => {
      removeNotification(id)
    }, duration)
  }

  const removeNotification = (id) => {
    const index = notifications.value.findIndex((n) => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
      console.log(`🗑️ Removed notification ${id}`)
    }
  }

  const requestNotificationPermission = async () => {
    if ("Notification" in window && Notification.permission === "default") {
      const permission = await Notification.requestPermission()
      return permission === "granted"
    }
    return Notification.permission === "granted"
  }

  // Manual sync trigger for testing
  const triggerSync = async () => {
    console.log("🧪 Manually triggering sync...")
    await forceSyncWhenOnline()
  }

  return {
    isOnline: readonly(isOnline),
    queuedReports: readonly(queuedReports),
    notifications: readonly(notifications),
    showNotification,
    removeNotification,
    requestNotificationPermission,
    triggerSync, // Export for manual testing
  }
}
